import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { sortMixes } from "@/lib/mixSort";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Save, RotateCcw, Coins, AlertTriangle, Upload } from "lucide-react";
import CsvImportDialog from "@/components/CsvImportDialog";

/**
 * v3.15 · Class-based Pricing Matrix
 *
 * Every mix × every pricing class (A/B/C/CASH/CONTRACT) in a single editable
 * table. Rates apply uniformly to every customer in that class. Fallback
 * ladder for a docket's rate is:
 *   customer.mix_prices[mix] → mix.class_rates[customer.class]
 *   → customer.price_per_m3 → mix.price_per_m3 → 220
 */

const CLASS_STYLES = {
  A:        { border: "border-emerald-500", text: "text-emerald-500", bg: "bg-emerald-500/10" },
  B:        { border: "border-sky-500",     text: "text-sky-500",     bg: "bg-sky-500/10" },
  C:        { border: "border-orange-500",  text: "text-orange-500",  bg: "bg-orange-500/10" },
  CASH:     { border: "border-yellow-500",  text: "text-yellow-500",  bg: "bg-yellow-500/10" },
  CONTRACT: { border: "border-blue-500",    text: "text-blue-500",    bg: "bg-blue-500/10" },
};

// v3.11 · Some class keys stay as their historical DB codes but the UI
// shows the friendlier accountant-side label. Kept as a map so future
// renames don't need to touch every reference.
const CLASS_LABELS = {
  CASH: "CASH / EFT",
};

export default function Pricing() {
  const [classes, setClasses] = useState([]);
  const [mixes, setMixes] = useState([]);
  const [edits, setEdits] = useState({});   // { mix_id: { A: "320", B: "300", ... } }
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [importOpen, setImportOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/pricing/matrix");
      setClasses(data.classes || []);
      setMixes(data.mixes || []);
      setEdits({});
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load pricing matrix");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const dirtyCount = useMemo(() => {
    let n = 0;
    for (const mix_id of Object.keys(edits)) {
      for (const cls of Object.keys(edits[mix_id])) {
        n++;
      }
    }
    return n;
  }, [edits]);

  const setCell = (mix_id, cls, value) => {
    setEdits((prev) => ({
      ...prev,
      [mix_id]: { ...(prev[mix_id] || {}), [cls]: value },
    }));
  };

  const cellValue = (mix, cls) => {
    if (edits[mix.id] && cls in edits[mix.id]) return edits[mix.id][cls];
    const v = mix.class_rates?.[cls];
    return v == null ? "" : String(v);
  };

  const save = async () => {
    if (dirtyCount === 0) return toast.info("No changes to save");
    setSaving(true);
    try {
      const rows = Object.entries(edits).map(([mix_id, rates]) => ({
        mix_id,
        rates: Object.fromEntries(
          Object.entries(rates).map(([c, v]) => [c, v === "" ? null : Number(v)])
        ),
      }));
      const { data } = await api.put("/pricing/matrix", rows);
      toast.success(`${data.updated} mix${data.updated === 1 ? "" : "es"} updated`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const reset = () => setEdits({});

  // Column-wise fill helper — apply the same rate for a whole class column
  const fillColumn = (cls) => {
    const raw = window.prompt(`Enter rate to apply to every mix under Class ${cls} (or leave blank to clear the column):`);
    if (raw === null) return;
    const val = raw.trim();
    if (val && (isNaN(Number(val)) || Number(val) < 0)) {
      return toast.error("Enter a positive number or leave blank");
    }
    const next = { ...edits };
    for (const m of mixes) {
      next[m.id] = { ...(next[m.id] || {}), [cls]: val };
    }
    setEdits(next);
  };

  const hasBlanks = mixes.some((m) => classes.some((c) => (m.class_rates?.[c] == null && !edits[m.id]?.[c])));

  return (
    <div className="min-h-full bg-surface" data-testid="pricing-matrix-page">
      <div className="border-b border-industrial px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 border border-primary-orange flex items-center justify-center">
            <Coins className="w-5 h-5 text-primary-orange" />
          </div>
          <div>
            <div className="mono uppercase text-[10px] tracking-widest text-muted-industrial">Section 03</div>
            <div className="text-lg font-bold uppercase tracking-wider">Pricing Matrix</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {hasBlanks && (
            <div className="flex items-center gap-1.5 text-xs mono uppercase tracking-widest text-yellow-500" title="Some class rates are empty — customers in those classes will fall back to the customer default">
              <AlertTriangle className="w-3.5 h-3.5" />
              Some blanks
            </div>
          )}
          <div className="mono text-[10px] uppercase tracking-widest text-muted-industrial">
            {dirtyCount > 0 ? `${dirtyCount} unsaved cell${dirtyCount === 1 ? "" : "s"}` : "All saved"}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setImportOpen(true)}
            data-testid="pricing-import"
            className="rounded-none border-industrial bg-transparent uppercase text-[10px] tracking-widest h-9"
          >
            <Upload className="w-3.5 h-3.5 mr-1.5" /> Import
          </Button>
          <CsvImportDialog entity="pricing" open={importOpen} onOpenChange={setImportOpen} onDone={load} />
          <Button
            variant="outline"
            size="sm"
            onClick={reset}
            disabled={dirtyCount === 0 || saving}
            data-testid="pricing-reset"
            className="rounded-none border-industrial bg-transparent uppercase text-[10px] tracking-widest h-9"
          >
            <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reset
          </Button>
          <Button
            size="sm"
            onClick={save}
            disabled={dirtyCount === 0 || saving}
            data-testid="pricing-save"
            className="rounded-none bg-primary-orange hover:bg-primary-orange/90 uppercase text-[10px] tracking-widest h-9 text-white"
          >
            <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? "Saving…" : `Save${dirtyCount ? ` (${dirtyCount})` : ""}`}
          </Button>
        </div>
      </div>

      <div className="p-6">
        {loading ? (
          <div className="mono text-sm text-muted-industrial">Loading…</div>
        ) : mixes.length === 0 ? (
          <div className="mono text-sm text-muted-industrial">No mixes configured yet. Create mixes on the Mix Designs page first.</div>
        ) : (
          <div className="border border-industrial overflow-hidden">
            <table className="w-full">
              <thead className="bg-surface-2">
                <tr className="border-b border-industrial">
                  <th className="text-left p-3 mono uppercase text-[10px] tracking-widest text-muted-industrial w-[26%]">Mix</th>
                  <th className="text-right p-3 mono uppercase text-[10px] tracking-widest text-muted-industrial w-[10%]" title="Fallback rate when no class rate is set">
                    Base $
                  </th>
                  {classes.map((c) => {
                    const st = CLASS_STYLES[c] || {};
                    const label = CLASS_LABELS[c] || `Class ${c}`;
                    return (
                      <th key={c} className={`text-center p-3 mono uppercase text-[10px] tracking-widest w-[12%] ${st.text}`}
                          title={c === "CASH" ? "Same rate for Cash and EFT walk-in sales" : ""}>
                        <div>{label}</div>
                        <button
                          type="button"
                          onClick={() => fillColumn(c)}
                          data-testid={`pricing-fill-${c}`}
                          className="mt-1 text-[9px] uppercase tracking-widest opacity-60 hover:opacity-100 hover:underline"
                        >
                          Fill column
                        </button>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {sortMixes(mixes).map((m) => (
                  <tr key={m.id} className="border-b border-industrial hover:bg-surface-2/50" data-testid={`pricing-row-${m.id}`}>
                    <td className="p-3">
                      <div className="font-semibold">{m.code}</div>
                      <div className="text-xs text-muted-industrial mono">{m.name}</div>
                    </td>
                    <td className="p-3 mono text-right text-muted-industrial">${Number(m.price_per_m3 || 0).toFixed(2)}</td>
                    {classes.map((c) => {
                      const st = CLASS_STYLES[c] || {};
                      const val = cellValue(m, c);
                      const dirty = edits[m.id] && c in edits[m.id];
                      const stored = m.class_rates?.[c];
                      const cleared = dirty && (val === "" || val === null);
                      return (
                        <td key={c} className="p-2">
                          <div className="relative">
                            <span className={`absolute left-2 top-1/2 -translate-y-1/2 mono text-xs ${st.text} opacity-60`}>$</span>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={val}
                              onChange={(e) => setCell(m.id, c, e.target.value)}
                              placeholder={stored == null ? "—" : ""}
                              data-testid={`pricing-cell-${m.id}-${c}`}
                              className={`w-full text-right mono py-1.5 pl-5 pr-2 bg-transparent border ${dirty ? `${st.border} ${st.bg}` : "border-industrial"} focus:outline-none focus:border-primary-orange`}
                            />
                            {cleared && (
                              <span className="absolute -bottom-3.5 right-0 mono text-[9px] uppercase tracking-widest text-red-500">clear</span>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-6 border border-industrial p-4 space-y-1 text-xs mono text-muted-industrial max-w-3xl">
          <div className="uppercase tracking-widest text-ink font-bold mb-2">How the rate is picked for a docket</div>
          <div>1. Customer’s per-mix override (customer edit form)</div>
          <div>2. <span className="text-primary-orange">Class rate on this matrix</span></div>
          <div>3. Customer default price_per_m3</div>
          <div>4. Mix default price_per_m3</div>
          <div className="pt-2 text-[10px]">Blank cells fall through to the next step — customers in an unconfigured class effectively use the customer/mix default.</div>
        </div>
      </div>
    </div>
  );
}
